import { Link } from 'react-router-dom';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', text: '#E8EDF5', sub: '#C5CDD8',
};

const S = {
  page: {
    minHeight: '100vh',
    background: C.ink,
    fontFamily: "'Cabinet Grotesk', sans-serif",
    color: C.text,
    lineHeight: 1.7,
  },
  header: {
    background: C.surface,
    borderBottom: `1px solid ${C.border}`,
    padding: '24px 40px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  back: {
    color: C.gold,
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'opacity 150ms',
  },
  logo: {
    fontFamily: "'Sora', sans-serif",
    fontWeight: 800,
    fontSize: '1rem',
    color: C.text,
    marginLeft: 'auto',
  },
  container: {
    maxWidth: 760,
    margin: '0 auto',
    padding: '48px 24px 80px',
  },
  h1: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 'clamp(1.6rem,3vw,2.2rem)',
    fontWeight: 800,
    color: C.text,
    marginBottom: 8,
  },
  updated: {
    color: C.muted,
    fontSize: '0.85rem',
    marginBottom: 40,
    paddingBottom: 24,
    borderBottom: `1px solid ${C.border}`,
  },
  h2: {
    fontFamily: "'Sora', sans-serif",
    fontSize: '1.1rem',
    fontWeight: 700,
    color: C.gold,
    marginTop: 36,
    marginBottom: 10,
  },
  p: {
    color: C.sub,
    fontSize: '0.9rem',
    marginBottom: 12,
  },
  ul: {
    color: C.sub,
    fontSize: '0.9rem',
    paddingLeft: 20,
    marginBottom: 12,
  },
  highlight: {
    background: 'rgba(240,180,41,0.08)',
    borderLeft: `3px solid ${C.gold}`,
    padding: '12px 16px',
    borderRadius: '0 8px 8px 0',
    marginBottom: 16,
    color: C.sub,
    fontSize: '0.875rem',
  },
};

export default function PrivacyPolicy() {
  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <Link to="/" style={S.back}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back to OptiKa
        </Link>
        <div style={S.logo}>
          Opti<span style={{ color: C.gold }}>Ka</span>
        </div>
      </div>

      <div style={S.container}>
        <h1 style={S.h1}>Privacy Policy</h1>
        <div style={S.updated}>Last updated: March 2026 &nbsp;·&nbsp; Effective: March 2026</div>

        <div style={S.highlight}>
          OptiKa is a business intelligence platform designed for Ghanaian small businesses. We take your privacy seriously and collect only the data needed to provide our services.
        </div>

        <h2 style={S.h2}>1. Information We Collect</h2>
        <p style={S.p}>We collect the following types of information:</p>
        <ul style={S.ul}>
          <li><strong>Account information:</strong> Name, email address, business name, and password (stored as a bcrypt hash — never in plaintext).</li>
          <li><strong>Business data:</strong> Inventory, sales, customer records, financial transactions, and staff information that you enter into OptiKa.</li>
          <li><strong>Staff records:</strong> For payroll features, sensitive fields such as SSNIT number, Ghana Card number, and bank account details may be stored with AES-256-GCM encryption when configured.</li>
          <li><strong>Usage data:</strong> Pages visited, features used, and error logs for product improvement.</li>
          <li><strong>Device information:</strong> Browser type, IP address, and device identifiers for security purposes.</li>
        </ul>

        <h2 style={S.h2}>2. How We Use Your Information</h2>
        <ul style={S.ul}>
          <li>To provide and operate the OptiKa service</li>
          <li>To send transactional emails (welcome, alerts, invoices)</li>
          <li>To process subscription payments via Paystack</li>
          <li>To detect and prevent fraud and security incidents</li>
          <li>To improve product features and fix bugs</li>
          <li>To comply with legal obligations under Ghanaian law</li>
        </ul>

        <h2 style={S.h2}>3. Data Storage and Security</h2>
        <p style={S.p}>Your data is stored on secure servers with the following protections:</p>
        <ul style={S.ul}>
          <li>All connections encrypted with TLS/HTTPS</li>
          <li>Passwords hashed with bcrypt (never stored in plaintext)</li>
          <li>Sensitive staff fields encrypted with AES-256-GCM</li>
          <li>JWT authentication with short-lived access tokens (15 minutes)</li>
          <li>Rate limiting to prevent brute-force attacks</li>
          <li>HTTP security headers via Helmet</li>
        </ul>

        <h2 style={S.h2}>4. Data Sharing</h2>
        <p style={S.p}>We do not sell your personal data. We share data only with:</p>
        <ul style={S.ul}>
          <li><strong>Paystack</strong> — for payment processing (subscription fees only)</li>
          <li><strong>Email service providers</strong> — to send transactional emails you trigger</li>
          <li><strong>Hosting providers</strong> (Railway, Supabase, Vercel) — infrastructure only</li>
          <li><strong>Legal authorities</strong> — when required by Ghanaian law or court order</li>
        </ul>

        <h2 style={S.h2}>5. Your Rights</h2>
        <p style={S.p}>You have the right to:</p>
        <ul style={S.ul}>
          <li>Access and download a copy of your business data</li>
          <li>Correct inaccurate information</li>
          <li>Delete your account and associated data</li>
          <li>Object to processing of your personal information</li>
          <li>Withdraw consent at any time</li>
        </ul>
        <p style={S.p}>To exercise these rights, email us at <a href="mailto:privacy@optika.app" style={{ color: C.gold }}>privacy@optika.app</a>.</p>

        <h2 style={S.h2}>6. Cookies and Local Storage</h2>
        <p style={S.p}>OptiKa uses browser local storage (not cookies) to store your authentication token and offline sale data. No third-party tracking cookies are used. You can clear this data at any time via your browser settings.</p>

        <h2 style={S.h2}>7. Data Retention</h2>
        <p style={S.p}>We retain your business data for as long as your account is active. If you cancel your account, data is deleted within 30 days unless required by law. Backup copies may persist for up to 90 days.</p>

        <h2 style={S.h2}>8. Children's Privacy</h2>
        <p style={S.p}>OptiKa is intended for business owners and is not directed at children under 18. We do not knowingly collect data from minors.</p>

        <h2 style={S.h2}>9. Changes to This Policy</h2>
        <p style={S.p}>We may update this policy periodically. We will notify you by email and display a notice in the app for material changes. Continued use after notice constitutes acceptance.</p>

        <h2 style={S.h2}>10. Contact</h2>
        <p style={S.p}>For privacy questions or data requests:</p>
        <ul style={S.ul}>
          <li>Email: <a href="mailto:privacy@optika.app" style={{ color: C.gold }}>privacy@optika.app</a></li>
          <li>Support: <a href="mailto:support@optika.app" style={{ color: C.gold }}>support@optika.app</a></li>
        </ul>

        <div style={{ marginTop: 48, paddingTop: 24, borderTop: `1px solid ${C.border}`, textAlign: 'center' }}>
          <Link to="/" style={{ color: C.gold, textDecoration: 'none', fontSize: '0.875rem', fontWeight: 600 }}>
            ← Back to OptiKa
          </Link>
          <div style={{ marginTop: 12, fontSize: '0.78rem', color: C.muted }}>© 2026 OptiKa Business · Built for Ghana 🇬🇭</div>
        </div>
      </div>
    </div>
  );
}
